import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowRight, Loader2, Share2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { partnerInviteInfoAppOnboardingPartnerInviteInfoGet } from '@/client/sdk.gen'
import type { PartnerInviteInfoResponse } from '@/client/types.gen'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useOnboardingStepViewed } from '@/hooks/use-analytics'
import { useOnboarding } from '@/hooks/use-onboarding'

export const Route = createFileRoute('/onboarding/partner-invite')({
  component: PartnerInviteLanding,
})

type ErrorState = 'token_expired' | 'token_invalid' | 'invite_not_found' | 'no_token' | 'unknown'

function getTokenFromUrl(): string | null {
  const params = new URLSearchParams(globalThis.location.search)
  return params.get('token')
}

function PartnerInviteLanding() {
  useOnboardingStepViewed('partner_invite')
  const navigate = useNavigate()
  const { updateState } = useOnboarding()
  const [token] = useState(getTokenFromUrl)
  const [error, setError] = useState<ErrorState | undefined>(token ? undefined : 'no_token')
  const [info, setInfo] = useState<PartnerInviteInfoResponse | undefined>()

  const hydrateState = useCallback((t: string, data: PartnerInviteInfoResponse) => {
    // Partner invitees go through the same onboarding as founders — we just prefill
    // what we know from the invite and carry the inviteToken through so the final
    // /complete request can attach shared accounts.
    updateState({
      onboardingMode: 'founder',
      inviteToken: t,
      email: data.inviteeEmail,
      firstName: data.inviteeFirstName,
      lastName: data.inviteeLastName,
      currentStep: 1,
    })
  }, [updateState])

  useEffect(() => {
    if (!token)
      return

    partnerInviteInfoAppOnboardingPartnerInviteInfoGet({
      query: { token },
    })
      .then(({ data, error: respError, response }) => {
        if (respError || !data) {
          if (response?.status === 401)
            setError('token_invalid')
          else if (response?.status === 410)
            setError('invite_not_found')
          else
            setError('unknown')
          return
        }
        hydrateState(token, data)
        setInfo(data)
      })
      .catch(() => {
        setError('unknown')
      })
  }, [token, hydrateState])

  const handleContinue = () => {
    navigate({ to: '/onboarding/email' })
  }

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">
              {error === 'invite_not_found' ? 'Invite expired' : 'Invalid invite link'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-sm text-muted-foreground">
              {error === 'token_expired' && 'This invite link has expired. Ask your partner to resend the invitation.'}
              {error === 'token_invalid' && 'This invite link is invalid. Ask your partner to resend the invitation.'}
              {error === 'invite_not_found' && 'This invitation has expired. Ask your partner to resend it.'}
              {error === 'no_token' && 'No invite token found. Check the link from your email.'}
              {error === 'unknown' && 'Something went wrong. Try again or ask your partner to resend the invitation.'}
            </p>
            <Button variant="outline" onClick={() => navigate({ to: '/login' })} className="w-full">
              Back to login
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!info) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading your invitation...</span>
        </div>
      </div>
    )
  }

  const accountLabel = info.accountCount === 1 ? 'account' : 'accounts'

  return (
    <div className="flex flex-1 items-center justify-center py-16">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Share2 className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">
            {`Welcome, ${info.inviteeFirstName}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 text-center">
          <p className="text-sm text-muted-foreground leading-relaxed">
            <strong className="text-foreground">{info.inviterOrgName}</strong>
            {' has invited you to SUPPLYCO with '}
            <strong className="text-foreground">
              {`${info.accountCount.toLocaleString()} shared ${accountLabel}`}
            </strong>
            . Set up your account to start working with them.
          </p>
          <Button onClick={handleContinue} className="w-full gap-1.5">
            Get started
            <ArrowRight className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
